import React, { startTransition, useEffect, useLayoutEffect, useState, useRef } from 'react'
import useStore from 'state/knovStore'
import cn from 'classnames'
import { isPanelCenter, SWIPE_SPEED_FAST, SWIPE_SPEED_SLOW } from 'state/imperativeApis/swiperApi'
import usePanelContext from 'refactor/hooks/usePanelContext'
import ErrorBoundary from 'components/shared/ErrorBoundary'
import useLogRender from 'refactor/hooks/useLogRender'
import PanelLoadingIndicator from 'components/panels/PanelLoadingIndicator'
import QuestContainer from './QuestContainer'
import QuestStream from './QuestStream'
import Streams from './Streams'
import Memes from './Memes'
import MusicStream from './MusicStream'
import MusicArtistProfile from './MusicArtistProfile'
import { isRadioStreamFilter } from './feedNewItemsEligibility'
import PanelStreamHeader from './PanelStreamHeader'
import { shouldPersistStreamHeader } from './streamHeaderPersistence'
import styles from 'components/panels/panel.module.scss'
import { isMobile } from 'lib/device'
import { cacheQuest, getCachedQuest } from 'state/cache'
import { pausePanelMedia } from 'components/panels/panelMediaPlayback'
import { serializeFilterKey } from 'refactor/hooks/api/useGetQuestsQueryKey'
import { ensureNewThreadDraft } from 'components/quests/newThreadDraftRecovery'
import questModel from 'components/quests/questModel'
import queryClient from 'api/queryClient'

const newThreadDraftRecoveryDependencies = {
    getCachedQuest,
    createDraft: (questId, filter) => questModel.newQuestFromFilter({ id: questId }, filter),
    cacheQuest,
}

function RecoverableQuestContainer({ filter, panel }) {
    useState(() =>
        ensureNewThreadDraft(
            {
                questId: filter?.questId,
                newThread: panel.newThread,
                draftFilter: panel.draftFilter,
            },
            newThreadDraftRecoveryDependencies,
        ),
    )

    return <QuestContainer filter={filter} />
}

function PauseOffscreenPanelMedia({ panelContentRef, panelIndex }) {
    const activeIndex = useStore(state => (state.numPanels === 1 ? state.activeIndex : null))
    const numPanels = useStore(state => state.numPanels)

    useEffect(() => {
        if (numPanels !== 1) return
        if (panelIndex == null || panelIndex === activeIndex) return
        pausePanelMedia(panelContentRef.current)
    }, [activeIndex, numPanels, panelIndex, panelContentRef])

    return null
}

/**
 * @typedef {Object} PanelProps
 * @property {boolean} [keyboardOpen]
 * @property {number} [keyboardHeight]
 */

/** @type {React.FC<PanelProps>} */
export default React.memo(function Panel(props) {
    const panelContext = usePanelContext()
    // Add any other contexts you're using
    // const someOtherContext = useContext(SomeOtherContext)

    useLogRender('Panel', props, {
        panelContext,
    })

    const { panel, hide, panelIndex, panelContentPrewarming = false } = panelContext
    const panelContentRef = useRef(null)

    const panelId = panel.panelId
    const filter = panel.filter
    const questId = filter?.questId
    const isQuest = questId
    const isStreamDiscovery = filter?.streams
    const isMemeDiscovery = filter?.memes
    // The profile's Music tab: the user's radio songs under the profile header.
    const musicArtistId = filter?.user_music
    const isQuestStream = !isQuest && !isStreamDiscovery && !isMemeDiscovery && !musicArtistId
    // Music is the Radio team stream with Radio / Artists tabs.
    const isMusicStream = isQuestStream && isRadioStreamFilter(filter)
    // Feed controls replace the content query in place. Keep the header above the
    // deferred-content boundary so its local UI state and DOM identity survive those swaps.
    // Participants still live with QuestStream because their header consumes its first quest.
    const persistStreamHeader = shouldPersistStreamHeader(filter)
    const contentKey = isQuest
        ? `quest:${questId}`
        : isQuestStream
        ? `stream:${serializeFilterKey(filter)}`
        : null
    const [readyContentKey, setReadyContentKey] = useState(() => (contentKey ? null : contentKey))
    const waitForPanelTransition = Boolean(
        contentKey &&
            panel.deferContentUntilTransition &&
            readyContentKey !== contentKey &&
            !panelContentPrewarming,
    )
    const cachedQuestAvailable = Boolean(
        questId && queryClient.getQueryData(['quest', String(questId)]),
    )

    // Predictive one-panel navigation must replace a deferred shell with real content
    // before the browser's hidden prewarm paint. A layout effect makes that promotion
    // synchronous with the prewarm commit instead of leaving it in a low-priority transition.
    useLayoutEffect(() => {
        if (!panelContentPrewarming || !contentKey || readyContentKey === contentKey) return
        setReadyContentKey(contentKey)
    }, [contentKey, panelContentPrewarming, readyContentKey])

    useEffect(
        function onMount() {
            function onHashChange() {
                if (isPanelCenter(panelId) && location.hash === '#play' && filter?.questId) {
                    const quest = getCachedQuest(filter.questId)
                    if (quest) {
                        openStoryModal(quest)
                    }
                }
            }
            window.onhashchange = onHashChange
            return () => {
                window.removeEventListener('hashchange', onHashChange)
            }
        },
        [filter?.questId],
    )

    const openStoryModal = useStore.getState().actions.modalActions.openStoryModal

    //console.log('RENDER PANEL', panelId, panel.empty, filter)

    useEffect(() => {
        if (!hide) return
        pausePanelMedia(panelContentRef.current)
    }, [hide])

    useEffect(() => {
        if (!contentKey) {
            if (readyContentKey != null) setReadyContentKey(null)
            return
        }
        if (readyContentKey === contentKey) return

        const mountPanelContent = () => {
            startTransition(() => setReadyContentKey(contentKey))
        }
        if (!waitForPanelTransition) {
            const frame = window.requestAnimationFrame(mountPanelContent)
            return () => window.cancelAnimationFrame(frame)
        }

        let completed = false
        let fallback = 0
        const completeAfterTransition = () => {
            if (completed) return
            completed = true
            window.clearTimeout(fallback)
            window.removeEventListener('knov-panel-nav-metric', handlePanelNavMetric)
            panel.setAnswerRevealReady?.('deferred-panel-content', true)
            useStore.getState().panels.editPanel(panelId, {
                deferContentUntilTransition: false,
            })
            mountPanelContent()
        }
        const handlePanelNavMetric = event => {
            const entry = event.detail
            if (entry?.name !== 'transition-end') return

            const detail = entry.detail || {}
            const targetPanelId = detail.targetPanelId || detail.panelId
            if (targetPanelId && targetPanelId !== panelId) return

            completeAfterTransition()
        }
        fallback = window.setTimeout(
            completeAfterTransition,
            (isMobile ? SWIPE_SPEED_FAST : SWIPE_SPEED_SLOW) + 100,
        )
        window.addEventListener('knov-panel-nav-metric', handlePanelNavMetric)

        return () => {
            window.clearTimeout(fallback)
            window.removeEventListener('knov-panel-nav-metric', handlePanelNavMetric)
        }
    }, [contentKey, panel, panelId, readyContentKey, waitForPanelTransition])

    useEffect(() => {
        panel.setAnswerRevealReady?.('deferred-panel-content', !waitForPanelTransition)
    }, [panel, waitForPanelTransition])

    // A stream card has already paid the cost to hydrate its quest preview. Mount that cached
    // content during the swipe so the destination panel can paint immediately and upgrade the
    // preview to a full thread in the background.
    const contentReady = !contentKey || readyContentKey === contentKey || cachedQuestAvailable
    const loadingShell = <PanelLoadingIndicator label="Loading panel" />

    return (
        <ErrorBoundary label={`panel ${panel.panelId}`}>
            <PauseOffscreenPanelMedia panelContentRef={panelContentRef} panelIndex={panelIndex} />
            <div
                ref={panelContentRef}
                style={{ visibility: hide ? 'hidden' : 'visible' }}
                data-panel-media-container="true"
                data-panel-media-panel-id={panelId}
                data-panel-media-panel-index={panelIndex}
                className={cn(
                    styles.panelComp,
                    props.keyboardOpen && styles.keyboardOpen,
                    'panel-comp new-quest quests-new-view',
                )}
            >
                {/* Spacer for clearance under the overlaid top bar (TDP / avatar).
                    The old per-panel "STREAM"/"THREAD" caps label was removed.
                    Behind fixed_header, PanelContainer pins the place bar over it. */}
                <div className={cn(styles.panelHeaderContainer)} />

                {persistStreamHeader && <PanelStreamHeader filter={filter} />}

                {isQuestStream &&
                    !isMusicStream &&
                    (contentReady ? (
                        <QuestStream filter={filter} hideSearchHeader={persistStreamHeader} />
                    ) : (
                        <div className={styles.streamLoadingShell}>{loadingShell}</div>
                    ))}

                {isMusicStream &&
                    (contentReady ? (
                        <MusicStream filter={filter} hideSearchHeader={persistStreamHeader} />
                    ) : (
                        <div className={styles.streamLoadingShell}>{loadingShell}</div>
                    ))}

                {musicArtistId && <MusicArtistProfile artistId={musicArtistId} panelId={panelId} />}

                {isStreamDiscovery && (
                    <Streams filter={filter} hideSearchHeader={persistStreamHeader} />
                )}

                {isMemeDiscovery && (
                    <Memes filter={filter} hideSearchHeader={persistStreamHeader} />
                )}

                {isQuest && (
                    <div className={cn(styles.activeQuestContainer, `panel-quest quest-container`)}>
                        {contentReady ? (
                            <RecoverableQuestContainer
                                key={questId}
                                filter={filter}
                                panel={panel}
                            />
                        ) : (
                            loadingShell
                        )}
                    </div>
                )}
            </div>

            {/* The blank run below the content: the New button / navbar zone at
                rest, or the keyboard while it is up. A spacer rather than padding,
                to handle mobile keyboard weirdness; its height comes from the
                panel container, which sizes it with resolvePanelBottomSpacers. */}
            <div className={styles.panelBottomSpacer} />
        </ErrorBoundary>
    )
})
